import { ArrowLeft, ChevronRight, LogOut } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { cn } from "@/lib/utils";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useWorkspaceStore } from "@/store/workspace-store";
import {
  resolveSettingsHref,
  visibleSettingsGroups,
} from "./settings-nav-config";
import { LogoutConfirmDialog } from "./logout-confirm-dialog";

export function SettingsMobileMenu() {
  const { workspaceId: urlWorkspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { orgId, isOrgAdmin } = useActiveOrg();
  const workspaceId = urlWorkspaceId || activeWorkspaceId || undefined;
  const [logoutOpen, setLogoutOpen] = useState(false);
  const visibleGroups = visibleSettingsGroups(isOrgAdmin);

  return (
    <div className="bg-background">
      {/* Top chrome: back-to-app */}
      <div className="sticky top-0 z-10 flex h-12 items-center border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <Link
          to={workspaceId ? `/${workspaceId}/dashboard` : "/workspaces"}
          className="flex h-9 items-center gap-1.5 -ml-2 px-2 rounded text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to app
        </Link>
      </div>

      {/* Page title */}
      <div className="px-4 pt-6 pb-3">
        <h1 className="text-lg font-semibold text-foreground">Settings</h1>
      </div>

      {/* Grouped section list */}
      <div className="space-y-5">
        {visibleGroups.map((group) => (
          <section key={group.title}>
            <div className="px-4 pb-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
              {group.title}
            </div>
            <div className="border-y border-border bg-card">
              {group.links.map((link, i) => (
                <Link
                  key={link.path}
                  to={resolveSettingsHref(link.path, workspaceId, orgId)}
                  className={cn(
                    "flex h-14 items-center gap-3 px-4 text-sm text-foreground transition-colors active:bg-foreground/[0.05]",
                    i !== group.links.length - 1 && "border-b border-border"
                  )}
                >
                  <link.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1">{link.label}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Log out — settings is the home for session actions. */}
      <div className="mt-5 pb-12">
        <div className="border-y border-border bg-card">
          <button
            type="button"
            onClick={() => setLogoutOpen(true)}
            className="flex h-14 w-full items-center gap-3 px-4 text-sm text-red-600 transition-colors active:bg-foreground/[0.05]"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">Log out</span>
          </button>
        </div>
      </div>

      <LogoutConfirmDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </div>
  );
}
